import React, { useEffect, useState } from 'react';
import { Database, Trash2, Clock, RefreshCw, Loader2, Send, Copy, Check, AlertTriangle, Users, Calendar } from 'lucide-react';
import { campaignsApi, CampaignRecord } from '../services/creatorApi';
import { crmContactsApi, BucketAssignProgress } from '../services/crmApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function SendBucketManager() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [clearingBucket, setClearingBucket] = useState<string | null>(null);
  const [clearProgress, setClearProgress] = useState<BucketAssignProgress | null>(null);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState<string | null>(null);
  const [clearingExpired, setClearingExpired] = useState(false);
  const fetchBuckets = async () => {
    setLoading(true);
    try {
      const all = await campaignsApi.list();
      // Filter to current team, with a Bucket_ID, status = Exported
      const filtered = all.filter(c => {
        if (c.Team !== currentWorkspace.name) return false;
        if (currentSubRegion && c.Region && c.Region !== currentSubRegion) return false;
        return !!c.Bucket_ID;
      });
      // Sort by Exported_At descending
      filtered.sort((a, b) => {
        const da = a.Exported_At ? new Date(a.Exported_At).getTime() : 0;
        const db = b.Exported_At ? new Date(b.Exported_At).getTime() : 0;
        return db - da;
      });
      setCampaigns(filtered);
    } catch (err) {
      console.error('Failed to load buckets:', err);
      toast.error('Failed to load send buckets');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchBuckets();
  }, [currentWorkspace.name, currentSubRegion]);
  const handleClearBucket = async (bucketId: string, campaignId: string) => {
    setClearingBucket(bucketId);
    setClearProgress(null);
    setShowClearConfirm(null);
    try {
      const result = await crmContactsApi.clearBucket(bucketId, progress => setClearProgress(progress));
      if (result.success) {
        // Update campaign status in Creator
        await campaignsApi.update(campaignId, {
          Status: 'Sent',
          Bucket_ID: ''
        });
        toast.success(`Cleared bucket from ${result.clearedCount} contacts`);
      } else {
        toast.error(`Failed to clear bucket: ${result.errors[0] || 'Unknown error'}`);
      }
      fetchBuckets();
    } catch (err) {
      console.error('Failed to clear bucket:', err);
      toast.error('Failed to clear bucket');
    } finally {
      setClearingBucket(null);
      setClearProgress(null);
    }
  };
  const handleClearExpired = async () => {
    setClearingExpired(true);
    setClearProgress(null);
    try {
      const result = await crmContactsApi.clearExpiredBuckets(progress => setClearProgress(progress));
      if (result.success) {
        toast.success(`Cleared ${result.clearedCount} expired bucket assignments`);
      } else {
        toast.error(`Failed: ${result.errors[0] || 'Unknown error'}`);
      }
      fetchBuckets();
    } catch (err) {
      console.error('Failed to clear expired:', err);
      toast.error('Failed to clear expired buckets');
    } finally {
      setClearingExpired(false);
      setClearProgress(null);
    }
  };
  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(id);
    setTimeout(() => setCopiedItem(null), 2000);
  };
  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) <= new Date();
  };
  const formatExpiry = (expiresAt?: string) => {
    if (!expiresAt) return 'No expiry';
    const date = new Date(expiresAt);
    const now = new Date();
    if (date <= now) return 'Expired';
    const hoursLeft = Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60));
    if (hoursLeft < 1) return 'Expiring soon';
    if (hoursLeft < 24) return `${hoursLeft}h remaining`;
    return `${Math.round(hoursLeft / 24)}d remaining`;
  };
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  const expiredCount = campaigns.filter(c => isExpired(c.Bucket_Expires_At)).length;
  return <div className="space-y-6" data-id="element-5859">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5860">
        <div className="min-w-0" data-id="element-5861">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5862">Send Buckets</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5863">
            Manage active audience buckets pushed to Zoho CRM. Clear buckets
            after sending campaigns.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-5864">
          <button onClick={fetchBuckets} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-5865">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-5866" />
          </button>
          {expiredCount > 0 && <button onClick={handleClearExpired} disabled={clearingExpired || !!clearingBucket} className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 disabled:opacity-50" data-id="element-5867">
              {clearingExpired ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-5868" /> : <Clock className="h-4 w-4" data-id="element-5869" />}
              Clear {expiredCount} Expired
            </button>}
        </div>
      </div>

      {/* Progress Bar (shown during clear operations) */}
      {(clearingBucket || clearingExpired) && clearProgress && <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2" data-id="element-5870">
          <div className="flex items-center justify-between text-sm" data-id="element-5871">
            <span className="text-purple-700 font-medium" data-id="element-5872">
              {clearingExpired ? 'Clearing expired buckets...' : `Clearing bucket ${clearingBucket}...`}
            </span>
            <span className="text-purple-900 font-mono text-xs" data-id="element-5873">
              Batch {clearProgress.currentBatch} / {clearProgress.totalBatches}
            </span>
          </div>
          <div className="w-full bg-purple-200 rounded-full h-2" data-id="element-5874">
            <div className="bg-purple-600 h-2 rounded-full transition-all duration-300" style={{
          width: `${Math.round(clearProgress.processedContacts / clearProgress.totalContacts * 100)}%`
        }} data-id="element-5875" />
          </div>
          <p className="text-xs text-purple-600" data-id="element-5876">
            {clearProgress.processedContacts} / {clearProgress.totalContacts}{' '}
            contacts processed
          </p>
        </div>}

      {/* Buckets Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-5877">
        {loading && campaigns.length === 0 ? <div className="p-12 text-center" data-id="element-5878">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-5879" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-5880">
              Loading send buckets...
            </p>
          </div> : campaigns.length === 0 ? <div className="p-12 text-center" data-id="element-5881">
            <Database className="mx-auto h-12 w-12 text-gray-400" data-id="element-5882" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-5883">
              No Active Buckets
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-5884">
              Export a campaign to create a send bucket in Zoho CRM.
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-5885">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-5886">
              <thead className="bg-gray-50" data-id="element-5887">
                <tr data-id="element-5888">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5889">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5890">
                    Bucket ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5891">
                    Recipients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5892">
                    Expiry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5893">
                    Exported
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5894">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-5895">
                {campaigns.map(campaign => {
              const expired = isExpired(campaign.Bucket_Expires_At);
              const isClearing = clearingBucket === campaign.Bucket_ID;
              return <tr key={campaign.ID} className={`${expired ? 'bg-red-50/50' : ''} hover:bg-gray-50`} data-id="element-5896">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-5897">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-5898">
                          {campaign.Campaign_Name}
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]" data-id="element-5899">
                          {campaign.Email_Subject}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-5900">
                        <div className="flex items-center gap-2" data-id="element-5901">
                          <code className="text-xs font-mono bg-purple-50 text-purple-700 px-2 py-1 rounded border border-purple-100" data-id="element-5902">
                            {campaign.Bucket_ID}
                          </code>
                          <button onClick={() => handleCopy(campaign.Bucket_ID, campaign.ID)} className="text-gray-400 hover:text-gray-600 p-1" title="Copy Bucket ID" data-id="element-5903">
                            {copiedItem === campaign.ID ? <Check className="h-3.5 w-3.5 text-green-500" data-id="element-5904" /> : <Copy className="h-3.5 w-3.5" data-id="element-5905" />}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-5906">
                        <div className="flex items-center gap-1.5 text-sm text-gray-700" data-id="element-5907">
                          <Users className="h-3.5 w-3.5 text-gray-400" data-id="element-5908" />
                          {campaign.Recipient_Count ? campaign.Recipient_Count.toLocaleString() : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-5909">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${expired ? 'bg-red-100 text-red-800' : campaign.Bucket_Expires_At ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700'}`} data-id="element-5910">
                          <Clock className="h-3 w-3" data-id="element-5911" />
                          {formatExpiry(campaign.Bucket_Expires_At)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5912">
                        {formatDate(campaign.Exported_At)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-5913">
                        <div className="flex items-center justify-end gap-2" data-id="element-5914">
                          <button onClick={() => handleCopy(`Bucket_ID equals ${campaign.Bucket_ID}`, `filter-${campaign.ID}`)} className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded text-xs font-medium" title="Copy Zoho Campaigns filter" data-id="element-5915">
                            {copiedItem === `filter-${campaign.ID}` ? 'Copied!' : 'Copy Filter'}
                          </button>
                          <button onClick={() => setShowClearConfirm(campaign.ID)} disabled={isClearing || !!clearingBucket} className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded disabled:opacity-50" title="Clear Bucket" data-id="element-5916">
                            {isClearing ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-5917" /> : <Trash2 className="h-4 w-4" data-id="element-5918" />}
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>

      {/* How it works */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-id="element-5919">
        <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-5920">
          How Send Buckets Work
        </h3>
        <ol className="space-y-2 text-sm text-gray-600" data-id="element-5921">
          <li className="flex gap-2" data-id="element-5922">
            <span className="font-medium text-gray-900" data-id="element-5923">1.</span>
            <span data-id="element-5924">
              Create a campaign and build an audience — this pushes a{' '}
              <code className="bg-white px-1 rounded text-xs border" data-id="element-5925">
                Bucket_ID
              </code>{' '}
              to each contact in Zoho CRM
            </span>
          </li>
          <li className="flex gap-2" data-id="element-5926">
            <span className="font-medium text-gray-900" data-id="element-5927">2.</span>
            <span data-id="element-5928">
              In Zoho Campaigns, create a segment with:{' '}
              <code className="bg-white px-1 rounded text-xs border" data-id="element-5929">
                Bucket_ID equals [your-bucket-id]
              </code>
            </span>
          </li>
          <li className="flex gap-2" data-id="element-5930">
            <span className="font-medium text-gray-900" data-id="element-5931">3.</span>
            <span data-id="element-5932">
              Send your campaign through Zoho Campaigns using that segment
            </span>
          </li>
          <li className="flex gap-2" data-id="element-5933">
            <span className="font-medium text-gray-900" data-id="element-5934">4.</span>
            <span data-id="element-5935">
              After sending, <strong data-id="element-5936">clear the bucket</strong> here to free
              contacts for future campaigns
            </span>
          </li>
        </ol>
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5937">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5938">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowClearConfirm(null)} data-id="element-5939" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-5940">
              <div className="flex items-center gap-4 mb-4" data-id="element-5941">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-5942">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-5943" />
                </div>
                <div data-id="element-5944">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-5945">
                    Clear Bucket
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-5946">
                    This will remove the Bucket ID from all contacts in CRM.
                    Only do this after you've sent the campaign.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-5947">
                <button onClick={() => setShowClearConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5948">
                  Cancel
                </button>
                <button onClick={() => {
              const campaign = campaigns.find(c => c.ID === showClearConfirm);
              if (campaign) handleClearBucket(campaign.Bucket_ID, campaign.ID);
            }} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700" data-id="element-5949">
                  Clear Bucket
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}